import { ResourceJSON, ResourceQueryJSON } from '@monorepo/next-lab'

interface SpecialCharacter {
  char: string
  encodeRegExp: RegExp
  decodeRegExp: RegExp
  encodedChar: string
}

export abstract class BaseStorageService /*implements StorageHandlerInterface*/ {
  protected debug = false
  private readonly specialChars: SpecialCharacter[] = [
    {
      char: '.',
      encodeRegExp: /\./g,
      decodeRegExp: /\\u002E/g,
      encodedChar: '\\u002E',
    },
    {
      char: '$',
      encodeRegExp: /\$/g,
      decodeRegExp: /\\u0024/g,
      encodedChar: '\\u0024',
    },
  ]

  private _providerId = ''

  get providerId(): string {
    return this._providerId
  }

  set providerId(value: string) {
    this._providerId = value
  }

  protected constructor() {}

  async initialize(): Promise<void> {
    // override this method, if there is any initialization to be done
  }

  clear(): void {
    // override this method, if there is any stored data to be cleared
  }

  abstract getDebugLabel(): string

  logDebug(...messages: any): void {
    if (this.debug) {
      console.log(`${this.getDebugLabel()}: `, ...messages)
    }
  }

  abstract createResourcePromise(resource: ResourceJSON): Promise<ResourceJSON>

  abstract deleteResourcePromise(resourceId: string): Promise<void>

  abstract queryResourcesPromise(query: ResourceQueryJSON, fetchFromServer: boolean): Promise<ResourceJSON[]>

  abstract readResourcePromise(resourceId: string, fetchFromServer: boolean): Promise<ResourceJSON>

  abstract resourceExistsPromise(resourceId: string, fetchFromServer: boolean): Promise<boolean>

  abstract updateResourcePromise(resource: ResourceJSON, async: boolean | undefined): Promise<ResourceJSON>

  protected queryResourcesFromCachePromise(
    query: ResourceQueryJSON,
    getAllResourceIds: () => IterableIterator<string>,
    getResourceById: (id: string) => ResourceJSON,
  ): ResourceJSON[] {
    this.validateQuery(query)
    const clonedQuery = this.cloneJSON(query)
    this.setDefaultQueryProperties(clonedQuery)
    const resourceFullFillsQuery = (resource: ResourceJSON): boolean => {
      if (clonedQuery.actorId && resource.metadata.actor.id !== clonedQuery.actorId) {
        return false
      }
      if (clonedQuery.appId && resource.metadata.generator.id !== clonedQuery.appId) {
        return false
      }
      if (clonedQuery.resourceType && resource.metadata.target.objectType !== clonedQuery.resourceType) {
        return false
      }
      if (
        clonedQuery.modifiedSince &&
        resource.metadata.publishedServer &&
        new Date(resource.metadata.publishedServer) < new Date(clonedQuery.modifiedSince)
      ) {
        return false
      }
      return true
    }
    const foundResources: ResourceJSON[] = []
    for (const resourceId of getAllResourceIds()) {
      const resource = getResourceById(resourceId)!
      if (resourceFullFillsQuery(resource)) {
        if (clonedQuery.returnMetadataOnly) {
          foundResources.push({ metadata: this.cloneJSON(resource.metadata) })
        } else {
          foundResources.push(this.cloneResource(resource))
        }
      }
    }
    if (clonedQuery.orderByModificationDate) {
      foundResources.sort((resource1: ResourceJSON, resource2: ResourceJSON): number =>
        resource2.metadata.published!.localeCompare(resource1.metadata.published!),
      )
    }
    this.logDebug('queryResourcesFromCachePromise', query, foundResources)
    if (clonedQuery.maxResults) {
      const pageNumber = clonedQuery.pageNumber ? clonedQuery.pageNumber : 0
      const beginIndex = pageNumber > 0 ? (pageNumber - 1) * clonedQuery.maxResults : 0
      const endIndex = beginIndex + clonedQuery.maxResults
      return foundResources.slice(beginIndex, endIndex)
    } else {
      return foundResources
    }
  }

  protected validateResource(resource: ResourceJSON, existing: boolean): void {
    if (!resource) {
      throw new Error(`resource may not be null or undefined`)
    }
    if (!resource.metadata) {
      throw new Error(`resource must have a metadata property`)
    }
    if (resource.metadata.provider?.id !== this.providerId) {
      throw new Error(`wrong provider id: ${resource.metadata.provider?.id}`)
    }
    if (existing && !resource.metadata.target) {
      throw new Error(`an existing resource must have a metadata.target property`)
    }
    if (existing && !resource.metadata.target.id) {
      throw new Error(`an existing resource must have a metadata.target.id property`)
    }
  }

  protected validateQuery(query: ResourceQueryJSON): void {
    const checkProperty = (name: keyof ResourceQueryJSON, requiredType: string): void => {
      const propertyType = typeof query[name]
      if (propertyType !== 'undefined') {
        if (propertyType !== requiredType) {
          throw new TypeError(`The type of query.${name} is ${propertyType}, but must be ${requiredType}`)
        }
        if (requiredType === 'string') {
          //
        }
        switch (requiredType) {
          case 'string':
            if ((query[name] as string).length === 0) {
              throw new TypeError(`The value of query.${name} may not be empty`)
            }
            break
          case 'number':
            if ((query[name] as number) < 1) {
              throw new TypeError(`The value of query.${name} (${query[name]}) must be greater than 0`)
            }
            break
          default:
        }
      }
    }
    checkProperty('actorId', 'string')
    checkProperty('appId', 'string')
    checkProperty('resourceType', 'string')
    checkProperty('orderByModificationDate', 'boolean')
    checkProperty('maxResults', 'number')
    checkProperty('pageNumber', 'number')
    checkProperty('modifiedSince', 'string')
    if (query.pageNumber && !query.maxResults) {
      throw new Error(`If pageNumber is specified, then maxResults must be specified to`)
    }
  }

  protected setDefaultQueryProperties(clonedQuery: ResourceQueryJSON): void {
    if (typeof clonedQuery.returnMetadataOnly === 'undefined') {
      clonedQuery.returnMetadataOnly = false
    }
  }

  protected cloneJSON<T>(json: T): T {
    return JSON.parse(JSON.stringify(json))
  }

  protected cloneResource(resource: ResourceJSON): ResourceJSON {
    const clonedResource: ResourceJSON = {
      metadata: this.cloneJSON(resource.metadata),
    }
    switch (typeof resource.content) {
      case 'undefined':
        break
      case 'object':
        clonedResource.content = this.cloneJSON(resource.content)
        break
      default:
        clonedResource.content = resource.content
    }
    return clonedResource
  }
}
